import axios from 'axios';
import qs from 'qs';
import { MULTIMEDIA_TYPE } from '@/constants/global';
import {
	CreateDigitalInputDto,
	CreateDriverInfoDto,
	CreateExtraInfoDto,
	CreateTankDto,
	DeleteExtraInfoDto,
	DeviceDto,
	DeviceVehicleFullInfoDto,
	DigitalInputDto,
	DriverInfoDetailsDto,
	DriverInfoDto,
	ExtraInfoDto,
	OdometerHistoryDto,
	UpdateDriverDeviceDto,
	UpdateDriverInfoDto,
	UpdateExtraInfoDto,
	UpdateTankSensorDto,
	VehicleFullInfoDto,
} from '@/features/devices/device.dto';
import { MultimediaType, TankConfigDto, TrackingHistoryDto, TrackingHistoryEventDto } from '@/features/map/map.type';
import { GetThermometersParams, ThermometerDto, ThermometerUpdateDto } from '@/features/sensors/dtos/thermometer.dto';
import { SpeedLimitService } from '@/OptimusRoutes/constants';
import { GoogleWeatherAlertsResponse } from '@/OptimusRoutes/pages/MainMap/types/googleWeatherAlerts.types';
import { downloadFile } from '@/utilities';
import { ApiResult, Filter, ServiceResponse } from '../../OptimusRoutes/interfaces';
import { UpdateModelProfileDto, UpdateModelProfileResponseDto } from '../modelProfile/modelProfile.dto';
import { MultimediaDto, MultimediaEventDto } from '../multimedia/multimedia.dto';
import {
	ChangeEventStatusParams,
	CreateOdometerHistory,
	GetTrailHistoryEventsParams,
	GetTrailHistoryParams,
	UploadDeviceFile,
	UploadDevicesResponse,
} from './device.types';

type Service = {
	get: (id: number) => Promise<ServiceResponse<DeviceDto>>;
	getMultimediaFiles: (
		id: number,
		startDate: string,
		endDate: string,
		type?: MultimediaType
	) => Promise<ServiceResponse<MultimediaDto[]>>;
	getEventsMultimedia: (
		id: number,
		startDate: string,
		endDate: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getEventsMultimediaByTrackingHistoryId: (
		id: number,
		trackingHistoryId: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getTrailHistory: (args: GetTrailHistoryParams) => Promise<ServiceResponse<TrackingHistoryDto[]>>;
	getTrailHistoryEvents: (args: GetTrailHistoryEventsParams) => Promise<ServiceResponse<TrackingHistoryEventDto[]>>;
	updateUnitDetails: (id: number, description: string, timeZoneId: number) => Promise<ServiceResponse>;
	uploadFile: (data: UploadDeviceFile) => Promise<ServiceResponse<UploadDevicesResponse>>;
	getThermometers: (params: GetThermometersParams) => Promise<ServiceResponse<ThermometerDto[]>>;
	createThermometer: (id: number, order: number) => Promise<ServiceResponse<ThermometerDto>>;
	updateThermometer: (
		id: number,
		thermometerId: number,
		update: ThermometerUpdateDto
	) => Promise<ServiceResponse<ThermometerDto>>;
	changePostedSpeedLimitStatus: (deviceId: number, speedLimitService: SpeedLimitService) => Promise<ServiceResponse>;
	deleteDeviceTrackingHistory: (id: number) => Promise<ServiceResponse>;
	deletePositionById: (deviceId: number, trackingId: string) => Promise<ServiceResponse>;
	changeEventStatus: (eventData: ChangeEventStatusParams) => Promise<ServiceResponse>;
	createOdometerHistory: (odometerHistory: CreateOdometerHistory) => Promise<ServiceResponse<OdometerHistoryDto>>;
	updateDevicesModelProfile: (body: UpdateModelProfileDto) => Promise<ServiceResponse<UpdateModelProfileResponseDto>>;
	shareDevicesWithClient: (deviceIdList: number[], clientIdList: number[]) => Promise<ServiceResponse>;
	stopSharingDeviceWithClients: (deviceId: number, clientIdList: number[]) => Promise<ServiceResponse>;
	exportDevices: (deviceIds: number[]) => Promise<void>;
	update: (deviceId: number, data: Partial<DeviceDto>) => Promise<ServiceResponse>;
	getDigitalInputs: (
		deviceId: number,
		filter: Filter<DigitalInputDto>
	) => Promise<ServiceResponse<ApiResult<DigitalInputDto[]>>>;
	searchDigitalInputs: (deviceIds: number[]) => Promise<ServiceResponse<ApiResult<DigitalInputDto[]>>>;
	createDigitalInput: (
		deviceId: number,
		digitalInput: CreateDigitalInputDto
	) => Promise<ServiceResponse<DigitalInputDto>>;
	deleteDigitalInput: (deviceId: number, eventId: number) => Promise<ServiceResponse>;

	createExtraInfo: (data: CreateExtraInfoDto) => Promise<ServiceResponse<ExtraInfoDto>>;
	updateExtraInfo: (deviceId: number, data: UpdateExtraInfoDto) => Promise<ServiceResponse<ExtraInfoDto[]>>;
	deleteExtraInfo: (data: DeleteExtraInfoDto) => Promise<ServiceResponse>;

	createDriverInfo: (driverInfo: CreateDriverInfoDto) => Promise<ServiceResponse<DriverInfoDto>>;
	updateDriverInfo: (
		deviceId: number,
		driverId: number,
		driverInfo: UpdateDriverInfoDto
	) => Promise<ServiceResponse<DriverInfoDto>>;
	updateDriverDevice: (
		deviceId: number,
		driverId: number,
		updateData: UpdateDriverDeviceDto
	) => Promise<ServiceResponse<DriverInfoDto>>;
	getDriverInfo: (deviceId: number) => Promise<ServiceResponse<DriverInfoDto | null>>;
	getDriversFullInfo: (deviceIds: number[]) => Promise<ServiceResponse<VehicleFullInfoDto[]>>;
	deleteDrivers: (driverInfoId: number[]) => Promise<ServiceResponse>;
	getVehicleFullInfo: (deviceId: number) => Promise<ServiceResponse<DeviceVehicleFullInfoDto>>;
	getDriverInfoById: (driverInfoId: number) => Promise<ServiceResponse<DriverInfoDetailsDto>>;
	updateLiveStreamAudio: (deviceId: number, isLiveStreamAudioOn: boolean) => Promise<ServiceResponse>;
	getLiveStreamAudioStatus: (deviceId: number) => Promise<ServiceResponse<boolean>>;
	getWeatherAlerts: (deviceId: number) => Promise<ServiceResponse<GoogleWeatherAlertsResponse>>;
	getTanks: (deviceId: number) => Promise<ServiceResponse<TankConfigDto[]>>;
	addTankConfiguration: (deviceId: number, tank: CreateTankDto) => Promise<ServiceResponse<TankConfigDto>>;
	updateTankConfiguration: (
		id: number,
		deviceId: number,
		tank: UpdateTankSensorDto
	) => Promise<ServiceResponse<TankConfigDto>>;
};

const get = async (id: number): Promise<ServiceResponse<DeviceDto>> => {
	const { data } = await axios.get<ServiceResponse<DeviceDto>>(`/v2/devices/${id}`);
	return data;
};

const exportDevices = async (deviceIds: number[]): Promise<void> => {
	const { data: file, headers } = await axios.post<BlobPart>(
		'/v2/devices/export',
		{ deviceIds },
		{
			responseType: 'blob',
		}
	);
	const url = window.URL.createObjectURL(new Blob([file]));
	downloadFile(url, headers);
};

const updateDevicesModelProfile = async (
	body: UpdateModelProfileDto
): Promise<ServiceResponse<UpdateModelProfileResponseDto>> => {
	const { data } = await axios.patch<ServiceResponse<UpdateModelProfileResponseDto>>(
		'/v2/devices/model-profiles',
		body
	);
	return data;
};
const getEventsMultimedia = async (
	id: number,
	startDate: string,
	endDate: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
	};

	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events`,
		options
	);
	return data;
};

const getMultimediaFiles = async (
	id: number,
	startDate: string,
	endDate: string,
	type: MultimediaType = MULTIMEDIA_TYPE.VIDEO
): Promise<ServiceResponse<MultimediaDto[]>> => {
	const options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
			type,
		},
	};
	const { data } = await axios.get<ServiceResponse<MultimediaDto[]>>(`/v2/devices/${id}/multimedia-files`, options);
	return data;
};

const getTrailHistory = async ({
	id,
	startDate,
	endDate,
	cancelToken,
}: GetTrailHistoryParams): Promise<ServiceResponse<TrackingHistoryDto[]>> => {
	const params = {
		'start-date': startDate,
		'end-date': endDate,
	};

	const { data } = await axios.get<ServiceResponse<TrackingHistoryDto[]>>(`/v2/devices/${id}/trail-history`, {
		params,
		cancelToken,
	});
	return data;
};

const getTrailHistoryEvents = async ({
	id,
	startDate,
	endDate,
	cancelToken,
}: GetTrailHistoryEventsParams): Promise<ServiceResponse<TrackingHistoryEventDto[]>> => {
	const params = {
		'start-date': startDate,
		'end-date': endDate,
	};

	const { data } = await axios.get<ServiceResponse<TrackingHistoryEventDto[]>>(
		`/v2/devices/${id}/trail-history/events`,
		{
			params,
			cancelToken,
		}
	);
	return data;
};

const getEventsMultimediaByTrackingHistoryId = async (
	id: number,
	trackingHistoryId: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events/${trackingHistoryId}`
	);
	return data;
};

const uploadFile = async (values: UploadDeviceFile): Promise<ServiceResponse<UploadDevicesResponse>> => {
	const formData = new FormData();
	(Object.keys(values) as (keyof UploadDeviceFile)[]).forEach((key) => {
		const value = values[key];
		if (key === 'file') {
			formData.append('file', values.file);
			return;
		}
		if (value !== undefined && value !== null) {
			formData.append(key, value.toString());
		}
	});
	const { data } = await axios.post<ServiceResponse<UploadDevicesResponse>>('/v2/devices/upload-file', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return data;
};

const updateUnitDetails = async (id: number, description: string, timeZoneId: number): Promise<ServiceResponse> => {
	const { data } = await axios.patch<ServiceResponse>(`/v2/devices/${id}/details`, {
		description,
		timeZoneId,
	});
	return data;
};

const changePostedSpeedLimitStatus = async (
	deviceId: number,
	speedLimitService: SpeedLimitService
): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/devices/${deviceId}/posted-speed-limit`, {
		speedLimitService,
	});
	return data;
};

const getThermometers = async ({
	id,
	includeAlertConfig,
	status,
}: GetThermometersParams): Promise<ServiceResponse<ThermometerDto[]>> => {
	const { data } = await axios.get<ServiceResponse<ThermometerDto[]>>(`/v2/devices/${id}/thermometers`, {
		params: { includeAlertConfig, status },
	});
	return data;
};

const createThermometer = async (id: number, order: number): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.post<ServiceResponse<ThermometerDto>>(`/v2/devices/${id}/thermometers`, { order });
	return data;
};
const updateThermometer = async (
	id: number,
	thermometerId: number,
	update: ThermometerUpdateDto
): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.patch<ServiceResponse<ThermometerDto>>(
		`/v2/devices/${id}/thermometers/${thermometerId}`,
		update
	);
	return data;
};

const deleteDeviceTrackingHistory = async (id: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${id}/tracking-history`);
	return data;
};

const deletePositionById = async (deviceId: number, trackingId: string): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${deviceId}/positions/${trackingId}`);
	return data;
};

const changeEventStatus = async (eventData: ChangeEventStatusParams): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>('/v2/devices/event-status', eventData);
	return data;
};

const createOdometerHistory = async (
	odometerHistory: CreateOdometerHistory
): Promise<ServiceResponse<OdometerHistoryDto>> => {
	const { data } = await axios.post<ServiceResponse<OdometerHistoryDto>>(
		'/v2/devices/odometer-history',
		odometerHistory
	);
	return data;
};

const shareDevicesWithClient = async (deviceIdList: number[], clientIdList: number[]): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>('/v2/devices/share-devices-with-client', {
		deviceIdList,
		clientIdList,
	});
	return data;
};

const stopSharingDeviceWithClients = async (deviceId: number, clientIdList: number[]): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>('/v2/devices/stop-sharing-devices-with-client', {
		data: { deviceId, clientIdList },
	});
	return data;
};

const update = async (deviceId: number, data: Partial<DeviceDto>): Promise<ServiceResponse> => {
	const { data: responseData } = await axios.patch<ServiceResponse>(`/v2/devices/${deviceId}`, data);
	return responseData;
};

const getDigitalInputs = async (
	deviceId: number,
	{ filter }: Filter<DigitalInputDto>
): Promise<ServiceResponse<ApiResult<DigitalInputDto[]>>> => {
	const params = { filter: JSON.stringify(filter) };
	const { data } = await axios.get<ServiceResponse<ApiResult<DigitalInputDto[]>>>(
		`/v2/devices/${deviceId}/digital-inputs?${qs.stringify(params)}`
	);
	return data;
};

const searchDigitalInputs = async (deviceIds: number[]): Promise<ServiceResponse<ApiResult<DigitalInputDto[]>>> => {
	const { data } = await axios.post<ServiceResponse<ApiResult<DigitalInputDto[]>>>(
		'/v2/devices/digital-inputs/search',
		{ deviceIds }
	);
	return data;
};

const createDigitalInput = async (
	deviceId: number,
	digitalInput: CreateDigitalInputDto
): Promise<ServiceResponse<DigitalInputDto>> => {
	const { data } = await axios.post<ServiceResponse<DigitalInputDto>>(
		`/v2/devices/${deviceId}/digital-inputs`,
		digitalInput
	);
	return data;
};

const deleteDigitalInput = async (deviceId: number, eventId: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${deviceId}/digital-inputs`, {
		data: { eventId },
	});
	return data;
};

const createExtraInfo = async (data: CreateExtraInfoDto): Promise<ServiceResponse<ExtraInfoDto>> => {
	const { data: response } = await axios.post<ServiceResponse<ExtraInfoDto>>('/v2/devices/extra-info', data);
	return response;
};

const updateExtraInfo = async (
	deviceId: number,
	data: UpdateExtraInfoDto
): Promise<ServiceResponse<ExtraInfoDto[]>> => {
	const { data: response } = await axios.patch<ServiceResponse<ExtraInfoDto[]>>(
		`/v2/devices/${deviceId}/extra-info`,
		data
	);
	return response;
};

const deleteExtraInfo = async ({ id, deviceId }: DeleteExtraInfoDto): Promise<ServiceResponse> => {
	const { data: response } = await axios.delete<ServiceResponse>(`/v2/devices/${deviceId}/extra-info/${id}`);
	return response;
};
const createDriverInfo = async (driverInfo: CreateDriverInfoDto): Promise<ServiceResponse<DriverInfoDto>> => {
	const { data } = await axios.post<ServiceResponse<DriverInfoDto>>('/v2/devices/drivers-info', driverInfo);
	return data;
};

const updateDriverInfo = async (
	deviceId: number,
	driverId: number,
	driverInfo: UpdateDriverInfoDto
): Promise<ServiceResponse<DriverInfoDto>> => {
	const { data } = await axios.put<ServiceResponse<DriverInfoDto>>(
		`/v2/devices/${deviceId}/drivers-info/${driverId}`,
		driverInfo
	);
	return data;
};

const updateDriverDevice = async (
	deviceId: number,
	driverId: number,
	updateData: UpdateDriverDeviceDto
): Promise<ServiceResponse<DriverInfoDto>> => {
	const { data } = await axios.put<ServiceResponse<DriverInfoDto>>(
		`/v2/devices/${deviceId}/drivers-info/${driverId}/assign`,
		updateData
	);
	return data;
};

const getDriverInfo = async (deviceId: number): Promise<ServiceResponse<DriverInfoDto | null>> => {
	const { data } = await axios.get<ServiceResponse<DriverInfoDto | null>>(`/v2/devices/${deviceId}/drivers-info`);
	return data;
};

const getDriversFullInfo = async (deviceIds: number[]): Promise<ServiceResponse<VehicleFullInfoDto[]>> => {
	const { data } = await axios.get<ServiceResponse<VehicleFullInfoDto[]>>('/v2/devices/drivers-info', {
		params: { deviceIds },
	});
	return data;
};

const deleteDrivers = async (driverInfoId: number[]): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>('/v2/devices/drivers-info', {
		data: { driverInfoId },
	});
	return data;
};

const getVehicleFullInfo = async (deviceId: number): Promise<ServiceResponse<DeviceVehicleFullInfoDto>> => {
	const { data } = await axios.get<ServiceResponse<any>>(`/v2/devices/${deviceId}/vehicle-info`);

	return data;
};

const getDriverInfoById = async (driverInfoId: number): Promise<ServiceResponse<DriverInfoDetailsDto>> => {
	const { data } = await axios.get<ServiceResponse<DriverInfoDetailsDto>>(`/v2/devices/drivers-info/${driverInfoId}`);
	return data;
};

const updateLiveStreamAudio = async (deviceId: number, isLiveStreamAudioOn: boolean): Promise<ServiceResponse> => {
	const { data } = await axios.patch<ServiceResponse>(`/v2/devices/${deviceId}/live-stream-audio`, {
		isLiveStreamAudioOn,
	});
	return data;
};

const getLiveStreamAudioStatus = async (deviceId: number): Promise<ServiceResponse<boolean>> => {
	const { data } = await axios.get<ServiceResponse<boolean>>(`/v2/devices/${deviceId}/live-stream-audio/status`);
	return data;
};

const getWeatherAlerts = async (deviceId: number): Promise<ServiceResponse<GoogleWeatherAlertsResponse>> => {
	const { data } = await axios.get<ServiceResponse<GoogleWeatherAlertsResponse>>(
		`/v2/devices/${deviceId}/weather-alerts`
	);
	return data;
};

const getTanks = async (deviceId: number): Promise<ServiceResponse<TankConfigDto[]>> => {
	const { data } = await axios.get<ServiceResponse<TankConfigDto[]>>(`/v2/devices/${deviceId}/tanks`);
	return data;
};

const addTankConfiguration = async (deviceId: number, tank: CreateTankDto): Promise<ServiceResponse<TankConfigDto>> => {
	const { data } = await axios.post<ServiceResponse<TankConfigDto>>(`/v2/devices/${deviceId}/tanks`, tank);
	return data;
};

const updateTankConfiguration = async (
	id: number,
	deviceId: number,
	tank: UpdateTankSensorDto
): Promise<ServiceResponse<TankConfigDto>> => {
	const { data } = await axios.patch<ServiceResponse<TankConfigDto>>(`/v2/devices/${deviceId}/tanks/${id}`, tank);
	return data;
};
const DeviceService: Service = {
	get,
	exportDevices,
	updateDevicesModelProfile,
	getMultimediaFiles,
	getEventsMultimedia,
	getEventsMultimediaByTrackingHistoryId,
	uploadFile,
	changePostedSpeedLimitStatus,
	getThermometers,
	createThermometer,
	updateThermometer,
	deleteDeviceTrackingHistory,
	deletePositionById,
	getTrailHistory,
	getTrailHistoryEvents,
	changeEventStatus,
	createOdometerHistory,
	updateUnitDetails,
	shareDevicesWithClient,
	stopSharingDeviceWithClients,
	update,
	getDigitalInputs,
	searchDigitalInputs,
	createDigitalInput,
	deleteDigitalInput,
	createExtraInfo,
	updateExtraInfo,
	deleteExtraInfo,
	createDriverInfo,
	updateDriverInfo,
	updateDriverDevice,
	getDriverInfo,
	getDriversFullInfo,
	deleteDrivers,
	getVehicleFullInfo,
	getDriverInfoById,
	updateLiveStreamAudio,
	getLiveStreamAudioStatus,
	getWeatherAlerts,
	getTanks,
	addTankConfiguration,
	updateTankConfiguration,
};

export default DeviceService;
